<template>
  <div
    id="content-container"
    class="content-container"
  >
    <div class="sticky top-0 px-6">
      <section class="my-4 flex items-center justify-between text-body-color">
        <div class="flex-col justify-between">
          <p class="mb-2 text-left">
            {{ data?.identity?.legal_name }}
          </p>
          <div class="flex">
            <p
              v-for="id in mainIdentifiers"
              :key="id?.is_type"
              class="paragraph mr-3"
            >
              {{ formatIsType(id?.is_type) }} {{ id?.value }}
            </p>
          </div>
        </div>

        <img
          v-if="brokerLogo"
          class="w-21 h-11"
          :src="brokerLogo"
          alt="broker logo"
        />
      </section>

      <section class="mb-4 mt-9 flex items-center justify-between">
        <h4
          v-if="stepName === 'Insurance-1' && hasFileAuto && !hasFileCargo"
          class="text-lg"
        >
          Cargo
        </h4>
        <h4
          v-else
          class="text-lg"
        >
          {{ stepTypes?.[formPosition]?.title }}
        </h4>
        <p class="paragraph">
          Step {{ evaluateStep }} of {{ evaluateStepTotal }}
        </p>
      </section>
      <p
        v-if="calculateSubTitle"
        class="-mt-5 text-left text-body-color"
      >
        {{ calculateSubTitle }}
      </p>
      <!-- <p
        v-if="stepName?.includes('Questions')"
        class="text-body-color text-left -mt-5"
      >Section {{ stepName.replace('Questions-', '') }}</p> -->
      <progress-indicator
        :current-step="stepTypes?.[formPosition]?.step"
        :auto-skip-stage-six="auto_skip_stage_six"
        :auto-skip-stage-seven="auto_skip_stage_seven"
        :auto-skip-stage-eight="auto_skip_stage_eight"
        :auto-skip-stage-nine="auto_skip_stage_nine"
      />
      <div class="-mt-3 hidden justify-between md:flex">
        <div
          v-for="(title, index) in stepTitles"
          :key="title"
          class="flex justify-center text-gray-over-dark"
        >
          <span class="relative h-3 w-3">
            <p
              class="absolute top-0 text-xxs"
              :class="
                index + 1 <= stepTypes?.[formPosition]?.step
                  ? 'font-bold text-dark-blue'
                  : 'font-medium text-gray-over-dark'
              "
              :style="progressTitleStyles(title)"
            >
              {{ title }}
            </p>
          </span>
        </div>
      </div>
    </div>
    <div
      v-if="mounted"
      class="w-full"
    >
      <!-- <form @submit.prevent="handleSubmit" novalidate="novalidate"> -->
      <form
        novalidate="novalidate"
        @submit.prevent
      >
        <div
          v-for="(step, index) in steps"
          :key="index"
          class="relative"
        >
          <Transition
            mode="out-in"
            :name="transition_name"
          >
            <component
              :is="step.component"
              v-show="formPosition === index"
              class="absolute inset-0 px-6 py-4 sm:py-6"
              :data="data"
              :update-field="updateField"
              :form-position="formPosition"
              :title="step.title"
              :step-name="stepName"
              :next-step="nextStep"
              :skip-step="skipStep"
              :screen-size="screenSize"
              @exit-modal="emitExitModal"
            />
          </Transition>
        </div>
        <!-- <div id="formNavigationButtons" class="md:fixed md:w-full md:max-w-3xl footer-container md:pr-8"></div> -->
      </form>
    </div>

    <error-report-modal
      v-if="showErrorModal"
      :step-name="stepName"
      :visible="showErrorModal"
      @close="onCloseModal"
      @success="onSuccessError"
      @toast="openToast"
    />
    <success-modal
      v-if="showSentModal"
      :visible="showSentModal"
      @close="onCloseSentModal"
    >
      Message Sent
    </success-modal>
    <need-help-modal
      v-if="showNeedHelpModal"
      :visible="showNeedHelpModal"
      @close="onCloseNeedHelpModal"
    />
  </div>
</template>

<script>
import { ref, shallowRef, computed } from 'vue';
import { useStore, mapGetters } from 'vuex';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toast-notification';

// import axios from "axios";
import Classifications from '@/components/steps/Identity/Classifications.vue';
import IdentityStepOne from '@/components/steps/Identity/IdentityStepOne.vue';
import IdentityStepTwo from '@/components/steps/Identity/IdentityStepTwo.vue';
import IdentityStepThree from '@/components/steps/Identity/IdentityStepThree.vue';
import IdentityStepFour from '@/components/steps/Identity/IdentityStepFour.vue';
import Operations from '@/components/steps/Operations/Operations.vue';
import Lanes from '@/components/steps/Lanes/Lanes.vue';
import ProgressIndicator from '@/components/ProgressIndicator.vue';
import ButtonMain from '@/components/Button.vue';
import ErrorReportModal from '@/components/modals/ErrorReportModal.vue';
import CertificationsStepOne from '@/components/steps/Certifications/CertificationsStepOne.vue';
import CertificationsStepTwo from '@/components/steps/Certifications/CertificationsStepTwo.vue';
import ContractStepOne from '@/components/steps/Contract/ContractStepOne.vue';
import ContractStepTwo from '@/components/steps/Contract/ContractStepTwo.vue';
import SuccessModal from '@/components/modals/SuccessModal.vue';
import Integrations from '@/components/steps/Integrations/Integrations.vue';
import PaymentStepOne from '@/components/steps/Payment/PaymentStepOne.vue';
import PaymentStepTwo from '@/components/steps/Payment/PaymentStepTwo.vue';
import PaymentStepThree from '@/components/steps/Payment/PaymentStepThree.vue';
import PaymentStepFour from '@/components/steps/Payment/PaymentStepFour.vue';
import PaymentStepFive from '@/components/steps/Payment/PaymentStepFive.vue';
import InsuranceStepOne from '@/components/steps/Insurance/InsuranceStepOne.vue';
import InsuranceStepTwo from '@/components/steps/Insurance/InsuranceStepTwo.vue';
import InsuranceStepThree from '@/components/steps/Insurance/InsuranceStepThree.vue';
import InsuranceStepFour from '@/components/steps/Insurance/InsuranceStepFour.vue';
import Questions from '@/components/steps/Questions/Questions.vue';
import OwnershipStepOne from '@/components/steps/Ownership/OwnershipStepOne.vue';
import OwnershipStepTwo from '@/components/steps/Ownership/OwnershipStepTwo.vue';
// import { toastResponse } from "@/common/handleServerResponse";
import { toastResponseInSetup } from '@/common/handleServerResponse';
import needHelp from '@/mixins/needHelp';
// import { formatErrorResponse } from "@/common/handleServerResponse";
import stepInformation from '@/common/stepInformation';

const classifications = shallowRef(Classifications);
const identityStepOne = shallowRef(IdentityStepOne);
const identityStepTwo = shallowRef(IdentityStepTwo);
const identityStepThree = shallowRef(IdentityStepThree);
const identityStepFour = shallowRef(IdentityStepFour);
const operations = shallowRef(Operations);
const lanes = shallowRef(Lanes);
const certificationsStepOne = shallowRef(CertificationsStepOne);
const certificationsStepTwo = shallowRef(CertificationsStepTwo);
const integrations = shallowRef(Integrations);
const contractStepOne = shallowRef(ContractStepOne);
const contractStepTwo = shallowRef(ContractStepTwo);
const paymentStepOne = shallowRef(PaymentStepOne);
const paymentStepTwo = shallowRef(PaymentStepTwo);
const paymentStepThree = shallowRef(PaymentStepThree);
const paymentStepFour = shallowRef(PaymentStepFour);
const paymentStepFive = shallowRef(PaymentStepFive);
const insuranceStepOne = shallowRef(InsuranceStepOne);
const insuranceStepTwo = shallowRef(InsuranceStepTwo);
const insuranceStepThree = shallowRef(InsuranceStepThree);
const insuranceStepFour = shallowRef(InsuranceStepFour);
const ownershipStepOne = shallowRef(OwnershipStepOne);
const ownershipStepTwo = shallowRef(OwnershipStepTwo);
const questions = shallowRef(Questions);

export default {
  name: 'FormFlow',
  components: {
    ButtonMain,
    ProgressIndicator,
    ErrorReportModal,
    SuccessModal,
  },
  mixins: [needHelp],
  emits: ['exit-modal'],
  data() {
    return {
      mounted: false,
      // transition_name: 'slide-fade', // 'slide-fade' forward, 'reverse-slide-fade backward'
      screenSize:
        window?.innerWidth >= 640
          ? window?.innerWidth <= 1024
            ? 'tablet'
            : 'desktop'
          : 'mobile',
      // classifications: null,
      // identityStepOne: null,
      // identityStepTwo: null,
      // operations: null,
      // lanes: null,
      // certificationsStepOne: null,
      // certificationsStepTwo: null,
      // integrations: null,
      // contractStepOne: null,
      // contractStepTwo: null,
      // paymentStepOne: null,
      // paymentStepTwo: null,
      // paymentStepThree: null,
      // paymentStepFour: null,
      // insuranceStepOne: null,
      // insuranceStepTwo: null,
      // insuranceStepThree: null,
      // insuranceStepFour: null,
      // questions: null,
      steps: [
        {
          title: 'Classifications',
          component: classifications,
          name: 'Classifications',
        },
        {
          title: 'Identity Step One',
          component: identityStepOne,
          name: 'Identity',
        },
        {
          title: 'Identity Step Two',
          component: identityStepTwo,
          name: 'Identity',
        },
        {
          title: 'Identity Step Three',
          component: identityStepThree,
          name: 'Identity',
        },
        {
          title: 'Identity Step Four',
          component: identityStepFour,
          name: 'Identity',
        },
        { title: 'Operations', component: operations, name: 'Operations' },
        { title: 'Lanes', component: lanes, name: 'Lanes' },
        {
          title: 'Certifications Step One',
          component: certificationsStepOne,
          name: 'Certifications',
        },
        {
          title: 'Certifications Step Two',
          component: certificationsStepTwo,
          name: 'Certifications',
        },
        {
          title: 'Insurance Step One',
          component: insuranceStepOne,
          name: 'Insurance',
        },
        {
          title: 'Insurance Step Two',
          component: insuranceStepTwo,
          name: 'Insurance',
        },
        {
          title: 'Insurance Step Three',
          component: insuranceStepThree,
          name: 'Insurance',
        },
        {
          title: 'Insurance Step Four',
          component: insuranceStepFour,
          name: 'Insurance',
        },
        {
          title: 'Payment Step One',
          component: paymentStepOne,
          name: 'Payment',
        },
        {
          title: 'Payment Step Two',
          component: paymentStepTwo,
          name: 'Payment',
        },
        {
          title: 'Payment Step Three',
          component: paymentStepThree,
          name: 'Payment',
        },
        {
          title: 'Payment Step Four',
          component: paymentStepFour,
          name: 'Payment',
        },
        {
          title: 'Payment Step Five',
          component: paymentStepFive,
          name: 'Payment',
        },
        {
          title: 'Questions Step One',
          component: questions,
          name: 'Questions',
        },
        {
          title: 'Integrations',
          component: integrations,
          name: 'Integrations',
        },
        {
          title: 'Ownership Step One',
          component: ownershipStepOne,
          name: 'Ownership',
        },
        {
          title: 'Ownership Step Two',
          component: ownershipStepTwo,
          name: 'Ownership',
        },
        {
          title: 'Contract Step One',
          component: contractStepOne,
          name: 'Contract',
        },
        {
          title: 'Contract Step Two',
          component: contractStepTwo,
          name: 'Contract',
        },
      ],
      currentStep: 0,
      temporaryEdit: false,
      error: false,
      dot_value: '',
      mc_value: '',
      // stepTitles: [
      //   "Identity",
      //   "Operations",
      //   "Lanes",
      //   "Certifications",
      //   "Insurance",
      //   "Payment",
      //   "Questionnaire",
      //   "Integrations",
      //   "Contract",
      // ],
    };
  },
  async created() {
    const clientId = localStorage.getItem('client_id');
    await this.$store.dispatch('brokers/getLogoByClientId', clientId);

    addEventListener('resize', () => {
      switch (true) {
        case window?.innerWidth <= 640:
          return (this.screenSize = 'mobile');
        case window?.innerWidth <= 1024:
          return (this.screenSize = 'tablet');
        default:
          return (this.screenSize = 'desktop');
      }
    });
  },
  computed: {
    shouldEnableCHRFlow() {
      return this.$store.getters['shouldEnableCHRFlow'];
    },
    transition_name() {
      return this.$store.state.formRoutes?.stepTransitionName || 'slide-fadeup';
    },
    ...mapGetters('insurance', ['getHasFileCargo', 'getHasFileAuto']),
    hasFileCargo() {
      return this.getHasFileCargo;
    },
    hasFileAuto() {
      return this.getHasFileAuto;
    },
    storedPaymentMethods() {
      return this.$store.state.payments?.activePaymentMethods;
    },
    stepTitles() {
      let titles = [
        'Identity',
        'Operations',
        'Lanes',
        'Certifications',
        'Insurance',
      ];
      if (this.auto_skip_stage_six == false) {
        titles.push('Payment');
      }
      if (this.auto_skip_stage_seven == false) {
        titles.push('Questionnaire');
      }
      if (this.auto_skip_stage_eight == false) {
        titles.push(this.shouldEnableCHRFlow ? 'Ownership' : 'Integrations');
      }
      if (this.auto_skip_stage_nine == false) {
        titles.push('Contract');
      }
      return titles;
    },
    firstTime() {
      return this.$store.state.firstTime;
    },
    // Company Type of the user completing the onboarding packet
    companyType() {
      return this.$store.state.users.currentUser?.company?.company_type;
    },
    onboardingPacket() {
      return this.$store.state.onboardingPacket;
    },
    currentSectionName() {
      return this.stepName?.split('-')?.[0];
    },
    // isButtonDisabled() {
    //   if(this.$store.state.payments?.payment_type === 'factoring'){
    //     return this.$store.state.submitButtonDisabled;
    //   }

    //   if (
    //     !this.$store.state.firstTime[this.currentSectionName] &&
    //     this.stepName !== "Payment-1" &&
    //     this.stepName !== "Payment-3" &&
    //     this.stepName !== "Insurance-1"
    //   ) {
    //     return false;

    //   }

    //   // if(this.stepName === 'Payment-3' &&  this.buttonsToDisplay === 'continue-edit'){
    //   //   return false;
    //   // }

    //   return this.$store.state.submitButtonDisabled;
    // },
    calculateSubTitle() {
      if (this.stepName?.includes('Insurance')) {
        return 'Insurance';
      } else if (this.stepName === 'Questions') {
        let currentSection = this.$store.state.questions?.section;
        let sectionNames = this.$store.state.questions?.section_names;
        return sectionNames[currentSection];
      }
      return '';
    },
    latestFactor() {
      return this.$store.state.factors?.latestFactor?.length
        ? this.$store.state.factors?.latestFactor?.[0]
        : null;
    },
    current_tax_form() {
      return this.$store.state.taxQuestions?.current_tax_form || null;
    },
    taxFormTitle() {
      if (this.current_tax_form && this.current_tax_form !== 'w_9') {
        const titles = {
          form_8233: 'Form 8233',
          w_8ben: 'W-8BEN',
          w_8ben_e: 'W-8BEN-E',
          w_8eci: 'W-8ECI',
        };
        return titles[this.current_tax_form];
      } else {
        return null;
      }
    },
    // buttonsToDisplay() {

    //   switch (true) {
    //     case this.stepName === "Classifications": return  'continue-save';
    //     case this.stepName === "Identity-1" && !this.error:
    //     case this.stepName === "Certifications-1" &&
    //       !this.error &&
    //       this.firstTime["Certifications"]:
    //       return "report-error";
    //     case this.stepName === "Payment-1":
    //       return "continue-save";
    //     case this.stepName === "Integrations":
    //       return "continue-save";
    //     case this.stepName === "Payment-4":
    //       return "continue-back";
    //     //   case this.stepName === "Payment-3" && this.storedPaymentMethods !== null && this.storedPaymentMethods?.length > 0:
    //     // return 'continue-edit'
    //     case this.stepName === 'Payment-2' && !this.$store.state.payments.edit.factor &&
    //     this.$store.state.payments?.payment_type === 'factoring' && (this.$store.state.payments?.saved_payment_term?.is_type === 'factoring' || !!this.$store.state.carriers?.carrierData?.factor):
    //       return 'continue-edit';

    //     case ["Payment-2", "Payment-3"].includes(this.stepName) &&
    //        ((this.firstTime["Payment"] && !this.latestFactor) ||
    //         this.$store.state.payments.edit.payment_method ||
    //         this.$store.state.payments.edit.factor):
    //       return "continue-save";
    //     case this.stepName === 'Payment-2' && this.$store.state.payments?.payment_type !== 'factoring' &&
    //     this.$store.state.payments.activePaymentMethods?.some(payment => payment?.is_type === this.$store.state.payments.payment_source && this.$store.state.payments.payment_source !== 'triumph_pay' ):
    //       return 'continue-edit';
    //     case this.stepName === 'Payment-2' && this.existSomeTriumphPayMethod && this.isTriumphPayAllowedByPaymentTerms && this.$store.state.payments?.payment_type !== 'factoring':
    //       return 'continue-edit';
    //     case this.stepName === "Payment-2" && this.$store.state.payments?.saved_payment_method == null:
    //       return "continue-save";
    //         //Start: Cases for Each case in Insurance.
    //     case this.stepName === "Insurance-1" && this.hasFileCargo && this.hasFileAuto && !this.firstTime.Insurance:
    //       return 'continue-edit';
    //     case this.stepName === "Insurance-1" && !this.hasFileAuto && this.hasFileCargo:
    //       return 'continue-save';
    //     case this.stepName === "Insurance-1" && this.hasFileAuto && !this.hasFileCargo:
    //       return 'continue-save';
    //     case this.stepName === "Insurance-1" && ((!this.hasFileAuto && !this.hasFileCargo) || this.firstTime.Insurance):
    //       return 'continue-save';
    //       //End: for Each case in Insurance.
    //     case this.stepName === "Payment-2" &&
    //         ((this.$store.state.payments?.payment_type === "factoring" &&
    //           !!this.data?.factor &&
    //           !this.$store.state.payments.edit.factor) ||
    //           (this.$store.state.payments.payment_type !== "factoring" &&
    //             !this.$store.state.payments.edit.payment_method)):
    //     case this.stepName === "Insurance-1" && !this.firstTime.Insurance:
    //         return "continue-edit";
    //     case !this.firstTime[this.currentSectionName] &&
    //       ![
    //         "Payment-1",
    //         "Payment-3",
    //         "Questions",
    //         "Contract-2",
    //       ].includes(this.stepName) && this.$store.state.payments?.payment_type !== "factoring":
    //       return "continue-edit";
    //     case this.stepName === "Contract-1" && this.taxFormTitle && this.firstTime.Contract:
    //       return "submit-save";
    //     case this.stepName === "Contract-1" && !this.firstTime.Contract:
    //       return "continue-edit";
    //     case this.stepName === "Contract-2":
    //       return "finish";
    //     default:
    //       return "continue-save";
    //   }
    // },
    evaluateStep() {
      switch (true) {
        // case !this.firstTime["Contract"] && this.stepName === "Contract-2":
        case this.stepName?.includes('Questions'):
          return this.$store.state.questions?.section_index + 1;
        case !this.firstTime['Insurance'] && this.stepName === 'Insurance-3':
        case this.$store.state.carriers.carrierData?.auto_liability_insurance
          ?.id > 0 && this.stepName === 'Insurance-3':
          return '2';
        case (this.$store.state.carriers.carrierData?.workers_comp_selections
          ?.id > 0 ||
          this.$store.state.carriers.carrierData?.auto_liability_insurance?.id >
            0) &&
          this.stepName === 'Insurance-4':
          return '3';
        default:
          return this.stepTypes?.[this.formPosition]?.subStep;
      }
    },
    evaluateStepTotal() {
      switch (true) {
        case !this.firstTime['Contract'] && this.stepName?.includes('Contract'):
          return '2';
        case this.stepName?.includes('Questions'):
          return this.$store.state?.questions?.total_sections;
        case !this.firstTime['Insurance'] &&
          this.stepName?.includes('Insurance'):
        case this.stepName?.includes('Insurance') &&
          this.$store.state.carriers.carrierData?.auto_liability_insurance?.id >
            0:
          return '3';
        case this.stepName?.includes('Identity') &&
          this.skipSubstepsThreeAndFourIdentity:
          return '2';
        case !this.firstTime['Certifications'] &&
          this.stepName === 'Certifications-1':
          return '1';
        case !this.firstTime['Payment'] &&
          this.stepName == 'Payment-2' &&
          this.$store.state.payments?.saved_payment_method?.is_type !== 'check':
          return '2';
        default:
          return this.stepTypes?.[this.formPosition]?.subStepTotal;
      }
    },
    brokerLogo() {
      return (
        this.$store.state.carriers?.carrierData?.connection?.issuer?.logo
          ?.url ||
        this.$store.state.brokers?.logo?.url ||
        null
      );
    },
    skipSubstepsThreeAndFourIdentity() {
      if (
        import.meta.env.VITE_DISABLE_RATE_CON_AND_INVOICING_CONTACT_STEPS == 1
      ) {
        return true;
      }
      return (
        this.$store.state.carriers.carrierDetails?.authority?.authorities
          ?.broker_authority_status !== 'Active'
      );
    },
    auto_skip_stage_six() {
      return (
        this.$store.state.carriers?.carrierData?.auto_skip_stage_six || false
      );
    },
    auto_skip_stage_seven() {
      if (this.$store.state.carriers?.carrierData?.auto_skip_stage_seven) {
        return true;
      } else if (
        this.$store.state.questions?.total_sections == 0 &&
        this.stepName?.includes('Questions')
      ) {
        return true;
      } else {
        return false;
      }
    },
    auto_skip_stage_eight() {
      if (this.shouldEnableCHRFlow) return false;

      return (
        this.$store.state.carriers?.carrierData?.auto_skip_stage_eight || false
      );
    },
    auto_skip_stage_nine() {
      return (
        this.$store.state.carriers?.carrierData?.auto_skip_stage_nine || false
      );
    },
    mainIdentifiers() {
      const mainIdentifiers = ['DOT', 'MC'];
      const identifiers =
        this.data.identity?.identifiers?.filter(
          (el) =>
            mainIdentifiers.includes(el?.is_type) || el?.is_manually_created
        ) ?? [];
      return identifiers;
    },
    latest_company_tax_form_submission() {
      return (
        this.$store.state.taxQuestions?.latest_company_tax_form_submission ||
        null
      );
    },
  },
  props: {
    nextStep: { type: Function },
    backStep: { type: Function },
    skipStep: { type: Function },
    formPosition: Number,
    stepTypes: Array,
    data: Object,
    updateField: { type: Function },
    openSaveModal: { type: Function },
    beginningStep: { type: Function },
    stepName: String,
    // updatePacket: Function,
    buttonLoading: Boolean,
    triggerLoadingBtn: Function,
    handleW8Submit: Function,
  },
  watch: {
    formPosition() {
      this.currentStep = this.formPosition;
      if (this.temporaryEdit) {
        this.temporaryEdit = false;
        let lastStep =
          this.stepTitles[
            this.stepTitles?.indexOf(this.currentSectionName) - 1
          ];
        this.$store.commit('firstTime/updateStepFirstTime', {
          step: lastStep,
          value: false,
        });
        this.$store.commit('firstTime/updateStepFirstTime', {
          step: this.currentSectionName,
          value: false,
        });
      }
      this.$store.commit('onboardingPacket/setViewedStepName', this.stepName);
    },
    '$store.state.carriers.carrierData': {
      deep: true,
      handler(val) {
        // Anything that needs to be updated when the carrierData changes
        if (val?.payment_term?.id > 0) {
          this.$store.commit('payments/setPaymentData', {
            data: val?.payment_term?.id,
            type: 'payment_term_id',
          });
          this.$store.commit('payments/setPaymentData', {
            data: val?.payment_term?.is_type,
            type: 'payment_type',
          });
          this.$store.commit('payments/setPaymentData', {
            data: val?.payment_term,
            type: 'saved_payment_term',
          });
          this.$store.commit('payments/setPaymentData', {
            data: val?.payment_term,
            type: 'selected_payment_term',
          });
          if (
            this.$store.state.payments?.latest_allowed_payment_method === null
          ) {
            this.$store.dispatch(
              'payments/getLatestAllowedPaymentMethod',
              val?.payment_term
            );
          }
        } else {
          this.$store.commit('payments/setPaymentData', {
            data: null,
            type: 'saved_payment_term',
          });
        }

        const filterName = this.shouldEnableCHRFlow
          ? 'Integrations'
          : 'Ownership';
        this.steps = this.steps.filter((step) => step.name !== filterName);

        if (val?.payment_method?.id > 0) {
          this.$store.commit('payments/setPaymentData', {
            data: val?.payment_method?.is_type,
            type: 'payment_source',
          });
          this.$store.commit('payments/setPaymentData', {
            data: val?.payment_method,
            type: 'saved_payment_method',
          });
        } else {
          this.$store.commit('payments/setPaymentData', {
            data: null,
            type: 'saved_payment_method',
          });
        }

        if (
          val?.company_question_sections?.length !=
          this.$store.state.questions?.total_sections
        ) {
          this.$store.commit(
            'questions/setTotalSections',
            val?.company_question_sections?.length
          );
        }
      },
      immediate: true,
    },
    latest_company_tax_form_submission(newVal) {
      if (
        newVal?.is_active == true &&
        [
          'w_8ben',
          'w_8ben_e',
          'w_8ben_e_substitute',
          'w_8eci',
          'form_8233',
        ].includes(newVal?.tax_form_is_type) &&
        this.$store.state.firstTime?.Contract == true
      ) {
        let allowed =
          this.$store.state.carriers?.carrierData?.connection?.issuer
            ?.allowed_w8_is_types ||
          this.$store.state.company?.companyData?.allowed_w8_is_types;
        if (allowed && allowed?.length > 0) {
          if (allowed.includes(newVal?.tax_form_is_type)) {
            this.$store.commit('firstTime/updateStepFirstTime', {
              step: 'Contract',
              value: false,
            });
          }
        } else {
          if (newVal?.tax_form_is_type !== 'w_8ben_e_substitute') {
            this.$store.commit('firstTime/updateStepFirstTime', {
              step: 'Contract',
              value: false,
            });
          }
        }
      }
    },
    // Remove Questionnaire from progress indicator when appropriate
    // auto_skip_stage_seven: {
    //   deep: true,
    //   handler(val) {
    //     var self = this;
    //     if (val == true) {
    //       const index = self.stepTitles.indexOf("Questionnaire");
    //       if (index > -1) {
    //         self.stepTitles.splice(index, 1);
    //       }
    //     }
    //   },
    //   immediate:true
    // },
    // Remove Integrations from progress indicator when appropriate
    //   auto_skip_stage_eight: {
    //     deep: true,
    //     handler(val) {
    //       var self = this;
    //       if (val == true) {
    //         const index = self.stepTitles.indexOf("Integrations");
    //         if (index > -1) {
    //           self.stepTitles.splice(index, 1);
    //         }
    //       }
    //     },
    //     immediate:true
    //   }
  },

  beforeMount() {
    this.$store.commit('firstTime/updateInitialFirstTime', this.getFirstTime());

    // const { stage_name } = this.$store.state.carriers.carrierData;

    // const findIndex = this.steps.findIndex((item) => {
    //   if (
    //     item?.title?.includes("Questions") &&
    //     stage_name === "Custom Questions"
    //   ) {
    //     return item;
    //   }
    //   return item?.title?.includes(stage_name);
    // });

    // Find the current step from the onboarding_packet
    // Start the form at that step
    // if (findIndex > 1) {
    //   this.$props.nextStep(findIndex, true);
    // }
  },

  mounted() {
    this.mounted = true;
    // console.log('FormFlow mounted');
    this.$store.commit(
      'addFormDebugMessage',
      'BEGINNING ONBOARDING SESSION FOR ONBOARDING PACKET ID ' +
        this.$router?.currentRoute?.value?.params?.id
    );
    // this.classifications = shallowRef(Classifications);
    // this.identityStepOne = shallowRef(IdentityStepOne);
    // this.identityStepTwo = shallowRef(IdentityStepTwo);
    // this.operations = shallowRef(Operations);
    // this.lanes = shallowRef(Lanes);
    // this.certificationsStepOne = shallowRef(CertificationsStepOne);
    // this.certificationsStepTwo = shallowRef(CertificationsStepTwo);
    // this.contractStepOne = shallowRef(ContractStepOne);
    // this.contractStepTwo = shallowRef(ContractStepTwo);
    // this.paymentStepOne = shallowRef(PaymentStepOne);
    // this.paymentStepTwo = shallowRef(PaymentStepTwo);
    // this.paymentStepThree = shallowRef(PaymentStepThree);
    // this.paymentStepFour = shallowRef(PaymentStepFour);
    // this.insuranceStepOne = shallowRef(InsuranceStepOne);
    // this.insuranceStepTwo = shallowRef(InsuranceStepTwo);
    // this.insuranceStepThree = shallowRef(InsuranceStepThree);
    // this.insuranceStepFour = shallowRef(InsuranceStepFour);
    // this.questions = shallowRef(Questions);
    // this.integrations = shallowRef(Integrations);
  },
  methods: {
    emitExitModal() {
      this.$emit('exit-modal');
    },
    formatIsType(type) {
      if (!type) return '';

      return type?.replace('_', ' ');
    },
    // TODO: Revisit this function if && !this.firstTime is needed
    async editCurrentStep() {
      if (this.stepName === 'Contract-2') {
        this.skipStep(this.formPosition - 1);
      } else if (this.stepName === 'Payment-2') {
        // if (this.$store.state.payments.payment_type === "factoring") {
        //   this.$store.dispatch("payments/updateNestedField", {
        //     path: "edit.factor",
        //     value: true,
        //   });
        // } else {
        this.skipStep(this.formPosition + 2);
        // }
      } else if (this.stepName === 'Certifications-1') {
        // Move user to 2nd certification step
        // await this.$props.nextStep();
        // this.temporaryEdit = true;
        await this.$store.commit('firstTime/updateStepFirstTime', {
          step: this.currentSectionName,
          value: true,
        });
      } else {
        // update currentstep here -- but go back to second time view on next step
        this.temporaryEdit = true;
        this.$store.commit('firstTime/updateStepFirstTime', {
          step: this.currentSectionName,
          value: true,
        });
      }
    },
    openToast() {
      this.onCloseModal();
      this.error = true;
      this.$toast?.open({
        message: 'Message Sent',
        type: 'success',
        duration: 5000,
        dismissible: true,
        position: 'top-right',
      });
    },
    progressTitleStyles(title) {
      if (title === 'Identity') {
        return 'left: 0;';
      } else if (
        title === 'Payment' &&
        this.stepTitles?.indexOf('Payment') == this.stepTitles?.length - 1
      ) {
        return 'right: -0.25rem;';
      } else if (
        title === 'Questionnaire' &&
        this.stepTitles?.indexOf('Questionnaire') == this.stepTitles?.length - 1
      ) {
        return 'right: -0.25rem;';
      } else if (
        title === 'Integrations' &&
        this.stepTitles?.indexOf('Integrations') == this.stepTitles?.length - 1
      ) {
        return 'right: -0.25rem;';
      } else if (title === 'Contract') {
        return 'right: -0.25rem;';
      } else {
        return 'left: 0.25rem; transform: translate(-50%, 0);';
      }
    },
    async verifyPayment() {
      const isTriumphPayRendered =
        this.$store.state.payments.rendered_payment?.[0]?.is_type ===
          'triumph_pay' ||
        this.$store.state.payments.rendered_payment?.is_type === 'triumph_pay';
      const TriumphPayMethod =
        this.$store.state.payments.rendered_payment?.[0] ||
        this.$store.state.payments.rendered_payment;
      let verification_id = this.$store.state.payments?.verification_id;
      let code = this.$store.state.payments?.verification_code;
      let res = await this.$store.dispatch('payments/verifyPaymentCode', {
        verification_id,
        code,
      });
      if (res.data.status === 'confirmed') {
        this.$store.dispatch('payments/updateNestedField', {
          path: 'errors',
          value: false,
        });
        this.$store.dispatch('payments/updateNestedField', {
          path: 'edit.payment_method',
          value: true,
        });

        if (
          (isTriumphPayRendered ||
            this.$store.state.payments?.latest_payment_method?.[0]?.is_type ===
              'triumph_pay') &&
          this.isTriumphPayAllowedByPaymentTerms
        ) {
          if (
            isTriumphPayRendered &&
            Number(TriumphPayMethod?.triumph_pay_select_carrier_rate) > 0
            // && this.$store.state.payments.payment_type !== 'factoring'
          ) {
            this.skipStep(this.formPosition - 3);
          } else {
            //Triumph Pay flow.
            this.$store.commit('toggleDisabledButton', {
              bool: true,
              source: 'FormFlow #1',
            });
            this.$store.commit('payments/setEditFactor', true);
            this.skipStep(this.formPosition - 2);
            return;
          }

          this.$store.commit('payments/setTriumphPayDeclined', true);
        } else {
          //Normal flows.
          if (this.$store.state.payments?.payment_type === 'factoring') {
            this.$store.commit('toggleDisabledButton', {
              bool: true,
              source: 'FormFlow #2',
            });
            this.$store.commit('payments/setEditFactor', true);
          }

          this.skipStep(this.formPosition - 2);
        }
      } else {
        this.$store.dispatch('payments/updateNestedField', {
          path: 'errors',
          value: true,
        });
      }
    },
    getFirstTime() {
      // Will set to false when condition below is met,
      // otherwise will remain true
      //
      // Identity: Dispatch, billing, and claims contacts are present
      // Operations: At least 1 equipment_type is present
      // Lanes: At least 1 latest_onboarding_lane_preference is present
      // Certifications: At least 1 untracked certification is present
      // Insurance (Certificates step): At least 1 policy is present
      // Insurance (Exclusions step): At least 1 item exclusions
      // Payment: A payment method is present
      // Questions: At least 1 question answer is present
      // Contract: A tax classification and at least 1 tax identifier is present

      const {
        identity,
        operations,
        lanes,
        certifications,
        insurance_policies,
        exclusions,
        payment_method,
        payment_term,
        factor,
      } = this.data;
      let firstTime = {};
      let dispatchContactPresent = false;
      let billingContactPresent = false;
      let claimsContactPresent = false;
      if (identity?.email_addresses?.length > 0) {
        dispatchContactPresent = identity.contacts.some(
          (el) => el.is_type === 'dispatch'
        );
        billingContactPresent = identity.contacts.some(
          (el) => el.is_type === 'billing'
        );
        claimsContactPresent = identity.contacts.some(
          (el) => el.is_type === 'claims'
        );
      }

      firstTime['Identity'] = !(
        dispatchContactPresent &&
        billingContactPresent &&
        claimsContactPresent
      );
      firstTime['Operations'] = !(operations?.equipment_types?.length > 0);
      firstTime['Lanes'] = !lanes?.latest_onboarding_lane_preferences?.length;
      firstTime['Certifications'] = !certifications.some((el) =>
        stepInformation.certifications.untracked_certifications.includes(
          el?.is_type
        )
      );
      firstTime['Insurance'] = !insurance_policies?.length;
      // firstTime["Insurance-Certificates"] = !insurance_policies?.length;
      firstTime['InsuranceExclusions'] = !exclusions?.length;
      firstTime['Payment'] =
        (payment_term?.is_type === 'factoring' && !factor) ||
        (payment_term?.is_type !== 'factoring' && !payment_method);
      firstTime['Questions'] = !this.$store.state.questions?.has_answers;
      firstTime['Contract'] =
        !identity?.tax_identifiers?.length || !identity?.tax_classification;
      // firstTime["Contract"] = !this.latest_company_tax_form_submission?.is_active;

      return firstTime;
    },
  },

  setup() {
    const store = useStore();
    const router = useRouter();
    const toast = useToast();

    // const showErrorModal = ref(false);
    // const showNeedHelpModal = ref(false);
    const showSentModal = ref(false);

    const showErrorModal = computed(() => store.state.showErrorModal);
    const showNeedHelpModal = computed(() => store.state.showNeedHelpModal);

    const finalButtonLoading = ref(false);

    // const currentStage = computed(
    //   () => store.state.onboardingPacket?.current_stage || null
    // );

    const existSomeTriumphPayMethod = computed(() =>
      store.state.payments.activePaymentMethods?.some(
        (payment) => payment?.is_type === 'triumph_pay'
      )
    );
    const isTriumphPayAllowedByPaymentTerms = computed(() =>
      store.state.payments?.payment_terms?.some((term) =>
        term?.allowed_payment_methods?.includes('triumph_pay')
      )
    );

    // const hasFileCargo = computed(() => {
    //   const cargo = store.state.insurance.insurancePolicies?.find(insurance => insurance?.is_type === 'motor_truck_cargo' && insurance?.status === 'active');
    //   return !!cargo;
    // })
    // const hasFileAuto = computed(() => {
    //   const auto = store.state.insurance.insurancePolicies?.find(insurance => insurance?.is_type === 'automobile_liability' && insurance?.status === 'active');
    //   return !!auto;
    // })

    const openErrorModal = () => {
      store.commit('setShowErrorModal', true);
    };
    const onCloseModal = () => {
      store.commit('setShowErrorModal', false);
    };

    const onSuccessError = () => {
      store.commit('setShowErrorModal', false);
      showSentModal.value = true;
    };

    // const onCloseNeedHelpModal = () => {
    //   store.commit('setShowNeedHelpModal', false);
    // };

    const onCloseSentModal = () => {
      showSentModal.value = false;
      // props.nextStep();
    };
    // const isEmptyObj = (obj) => {
    //   if(!obj) return true;
    //   for(var prop in obj) {
    //     if(Object.prototype.hasOwnProperty.call(obj, prop)) {
    //       return false;
    //     }
    //   }
    //   return JSON.stringify(obj) === JSON.stringify({});
    // };
    // const isPaymentMethodParamEmpty = (paymentMethod) => {
    //   if(!paymentMethod) return true;
    //   if (!("payment_method" in paymentMethod) || isEmptyObj(paymentMethod?.payment_method)) {
    //     return true;
    //   }
    //   return false;
    // };

    /**
     * Create a payment method according to the information filled in the form.
     * @param {*} paymentMethod The body of the request to be made.
     * @param {*} onboardingPacketId The ondboarding packet id.
     * @returns Returns true if it was possible to create the payment method or false if it was not possible and there was an error.
     */
    const isPaymentMethodAllowed = (requestedPaymentMethod) => {
      const paymentTerms = store.state.payments?.payment_terms;
      return paymentTerms?.some((payment) =>
        payment?.allowed_payment_methods?.includes(requestedPaymentMethod)
      );
    };
    // const createPaymentMethod = async (paymentMethod,onboardingPacketId) => {
    //    if (isPaymentMethodParamEmpty(paymentMethod)) {
    //     /**
    //      * The payment_method parameter is require in order to continue with the next step.
    //      */
    //     toast?.open({
    //       message: "Something went wrong with your request. Please return to the payment method selection screen and try again",
    //       position: 'top-right',
    //       duration: 5000
    //     });
    //     // Not show loading icon since the method failed.
    //     props.triggerLoadingBtn(false);
    //     return false;
    //   }
    //   let paymentResponse = null;

    //   try {
    //     paymentResponse =  await store.dispatch("payments/createPaymentMethod", {
    //                         paymentMethod,
    //                         onboardingPacketId,
    //                       });
    //                         await store.dispatch('payments/getActivePaymentMethods', onboardingPacketId)
    //     if([401, 403, 404, 422].includes(paymentResponse?.status)){
    //       throw new Error('An error has ocurred');
    //     }
    //   } catch (error) {
    //     if(!paymentResponse) paymentResponse = error?.response;
    //     if(paymentResponse) {
    //       toast?.open({
    //         message: formatErrorResponse(paymentResponse),
    //         position: 'top-right',
    //         duration: 5000
    //       });
    //     }
    //     // If there is an error trying to create the payment methos do not allow
    //     // the user to continue with the next step.
    //     return false;
    //   }
    //   return true;
    // };

    // const companyType = ()=> {
    //   return store.state.users.currentUser.company.company_type;
    // }

    // const onboardingPacket = () =>{
    //   return store.state.onboardingPacket;
    // }

    // Execute the contract for the onboarding packet
    // and complete the packet.  Handle the logic for
    // Directing the user to the next step depending on
    // the company type and the status of the packet
    const executeContractAndCompletePacket = async () => {
      // Execute contract API call
      finalButtonLoading.value = true;
      const executeRes = await store.dispatch(
        'onboardingPacket/executeContract'
      );
      // console.log(executeRes);
      if ([200, 201].includes(executeRes?.status)) {
        // console.log('packet completed', companyType, onboardingPacket.id)
        switch (store.state.users.currentUser?.company?.company_type) {
          case 'dispatcher':
            // Dispatchers don't follow the OAuth 2 flow, they go to a status
            // Screen for the packet
            console.log('dispatcher completed packet; redirecting to status');

            if (
              store.state.onboardingPacket?.status === 'complete' ||
              store.state.onboardingPacket?.status ===
                'held_for_carrier_approval'
            ) {
              return router.push({ name: 'Dispatcher Dashboard' });
            }
            return router.push({
              name: 'Dispatcher Onboarding Packet Status',
              params: { id: store.state.onboardingPacket?.id },
            });
          case 'broker':
          case 'carrier':
            return completePacketAsCarrier();
        }
      } else {
        console.log('there was an error');
        // There was an error
        toastResponseInSetup(toast, executeRes, null);
        finalButtonLoading.value = false;
      }
    };

    const completePacketAsCarrier = async () => {
      console.log(
        'completing packet as a carrier',
        store.state.onboardingPacket
      );
      if (store.state.onboardingPacket?.status != 'complete') {
        console.log('packet is incomplete; redirect to status screen');
        router.push({
          name: 'Carrier Onboarding Packet Status',
          params: { id: store.state.onboardingPacket?.id },
        });
      } else {
        console.log(
          'packet is complete; redirect to createAuthorization if present'
        );

        // If they came in through the OAuth process,
        // then then they will be redirected to the /oauth/authorizations
        // endpoint with the correct credentials
        let redirectAfterSignIn = JSON.parse(
          localStorage.getItem('redirect_url_after_sigin')
        );
        if (redirectAfterSignIn) {
          localStorage.removeItem('redirect_url_after_sigin');
          return router.push(redirectAfterSignIn);
        } else {
          router.push({
            name: 'Carrier Onboarding Packet Status',
            params: { id: store.state.onboardingPacket?.id },
          });
        }
      }
    };

    // Handle submit to server
    /*const handleSubmit = async (e) => {
      const storedPaymentMethod = store.state.payments?.activePaymentMethods
      const brokerHasTriumphPayTerm = store.state.payments?.payment_terms?.some(term => term?.allowed_payment_methods?.includes('triumph_pay'));
      const isTriumphPay = store.state.payments?.activePaymentMethods?.some(payment => payment?.is_type === 'triumph_pay');
      const isTriumphPayAllowed = store.state.payments?.activePaymentMethods?.some(payment => payment?.triumph_pay_wallet_destination_type === store.state.payments?.payment_source);
      console.log('handleSubmit called')
      props.triggerLoadingBtn();
      const { auto_skip_stage_six, auto_skip_stage_seven, auto_skip_stage_eight } = store.state.carriers.carrierData;
      console.log('auto skip stages six, seven and eight', auto_skip_stage_six, auto_skip_stage_seven, auto_skip_stage_eight)
      // Put a conditional for when the onboarding packet on the frontend is in stage 9 but
      // the server is in stage 8 to force calling next() and then executeContractAndCompletePacket()
      if (props.formPosition === props.stepTypes?.length - 1 && currentStage.value === 8) {
        const onboarding_id = store.state.carriers.carrierData?.id;
        const nextStepRes = await store.dispatch(
          "onboardingPacket/nextStepPatch",
          {
            id: onboarding_id,
          }
        );
        console.log('PRIOR STAGE 8 ... SERVER IS NOW ON STAGE',nextStepRes?.data?.stage)
        if (nextStepRes?.data?.stage == 9) {
          return executeContractAndCompletePacket();
        }
      } else if (props.formPosition === props.stepTypes?.length - 1 && currentStage.value === 9) {
        // Do not execute contract endpoint before stage 9
        console.log('executing contract and completing packet')
        return executeContractAndCompletePacket();
      } else if (props.stepName === "Questions") {
        console.log('custom questions section')
        let currentSection = store.state.questions?.section;
        let currentSectionIndex = store.state.questions?.section_index;
        let sectionOrder = store.state.questions?.section_order;
        let totalSections = store.state.questions?.total_sections;
        console.log('current and total sections', currentSection, totalSections);
        if (currentSection < totalSections) {
          store.commit("questions/setSectionIndex", currentSectionIndex + 1);
          store.commit("questions/setSection", sectionOrder[currentSectionIndex + 1]);
          // scroll to the next section after the first question section
          window.scrollTo(0, 0);

          if (
            store.state.onboardingPacket?.onboardingPacketBody
              ?.onboarding_packet
          ) {
            props.updatePacket(props.stepTypes[props.formPosition]);
          }
          props.triggerLoadingBtn(false);
        } else {
          if (auto_skip_stage_eight) {
            console.log('auto_skip_stage_eight is TRUE call props.nextStep with argument 2')
            await props.nextStep(2);
          } else {
            console.log('auto_skip_stage_eight is FALSE call props.nextStep with argument 2')
            props.nextStep();
          }
        }
      }
      else if(props.stepName === 'Insurance-4' && auto_skip_stage_six == true) {
        console.log('auto_skip_stage_six is TRUE, skipping payment step');
        if(!auto_skip_stage_seven) {
          console.log('do not auto skip stage seven')
          await props.nextStep(5);
        } else {
          // We need to request the updated packet again in case company question sections become required
          // via company question section conditions after payment data is submitted
          console.log('requesting updated packet to ensure auto_skip_stage_seven value');
          await store.dispatch("onboardingPacket/getOnboardingPacketById", { "id": store.state.onboardingPacket?.id });
          const recheck_auto_skip_stage_seven = store.state.carriers.carrierData?.auto_skip_stage_seven;
          console.log('recheck_auto_skip_stage_seven', recheck_auto_skip_stage_seven);
          // console.log('updated carrierData');
          // console.dir(store.state.carriers.carrierData);
          if (!recheck_auto_skip_stage_seven) {
            console.log('do NOT auto skip stage seven (after recheck)');
            await props.nextStep(5);
          } else {
            console.log('do auto skip stage seven')
            if (auto_skip_stage_eight) {
              console.log('do auto skip stage eight')
              // Skip question section and integration section if there aren't questions or integrations for the current Broker.
              await props.nextStep(7);
            } else {
              console.log('do NOT auto skip stage eight')
              // Skip question section if there aren't questions for the current Broker.
              await props.nextStep(6);
            }
            // Because the question section is escaped, the following function (updatePacket) calls the nextStepPatch function simulating the click that would occur in the question section
            console.log('call updatePacket', props.stepTypes[props.formPosition])
            // TODO - Jordan needs to validate the logic of this code
            props.updatePacket(props.stepTypes[props.formPosition]);
          }
        }
      }
      else if(props.stepName === 'Payment-2' && brokerHasTriumphPayTerm &&
                isTriumphPay && isTriumphPayAllowed && store.state.payments.secondVerificationPayment === false &&
                store.state.payments?.payment_source === 'triumph_pay' && store.state.payments.payment_type !== 'factoring' ){
        console.log('1. Payment-2 T_Pay allowed')
        store.commit('payments/setReview');
        store.commit('payments/setVerificationSecond', true);
        props.triggerLoadingBtn(false);

      }else if (props.stepName  ===  'Payment-2' && storedPaymentMethod?.some(payment => payment?.is_type === store.state.payments?.payment_source &&
      store.state.payments.secondVerificationPayment === false && store.state.payments.payment_type !== 'factoring')){
        if (store.state.payments?.edit?.check_ach == true) {
          // editing ach after verification
          await props.nextStep();
        } else {
          console.log('2. Payment-2 allowed')
          let paymentStored = false;
          storedPaymentMethod.forEach(payment => {
            if(payment.is_type === store.state.payments?.payment_source){
              paymentStored = true;
            }
          })
          if(paymentStored){
            console.log('paymentStored == true');
            store.commit('payments/setReview');
            store.commit('payments/setVerificationSecond', true)
            props.triggerLoadingBtn(false);
          }
        }
      }else if(props.stepName === 'Payment-2' && (!brokerHasTriumphPayTerm || (brokerHasTriumphPayTerm && !isTriumphPay)) && store.state.payments.payment_type !== 'factoring' &&
                store.state.payments.activePaymentMethods.some(payment => payment?.is_type === store.state.payments.payment_source)){
        console.log('Payment-2 without T_Pay but matched')
        const paymentMethodId = store.state.payments.activePaymentMethods.find(payment => payment?.is_type === store.state.payments.payment_source)
          if(paymentMethodId?.id > 0){
            const body = {
              payment_method_id: paymentMethodId?.id,
              factor_id: null,
            }
            store.dispatch("onboardingPacket/setOnboardingBody", {onboarding_packet: body});
            await props.updatePacket(props.stepTypes[props.formPosition]);
        }

        if(!auto_skip_stage_seven) {
          console.log('do not auto skip stage seven')
          await props.nextStep(3);
        } else {
          // We need to request the updated packet again in case company question sections become required
          // via company question section conditions after payment data is submitted
          console.log('requesting updated packet to ensure auto_skip_stage_seven value');
          await store.dispatch("onboardingPacket/getOnboardingPacketById", { "id": store.state.onboardingPacket?.id });
          const recheck_auto_skip_stage_seven = store.state.carriers.carrierData?.auto_skip_stage_seven;
          console.log('recheck_auto_skip_stage_seven', recheck_auto_skip_stage_seven);
          // console.log('updated carrierData');
          // console.dir(store.state.carriers.carrierData);
          if (!recheck_auto_skip_stage_seven) {
            console.log('do NOT auto skip stage seven (after recheck)');
            await props.nextStep(3);
          } else {
            console.log('do auto skip stage seven')
            if (auto_skip_stage_eight) {
              console.log('do auto skip stage eight')
              // Skip question section and integration section if there aren't questions or integrations for the current Broker.
              await props.nextStep(5);
            } else {
              console.log('do NOT auto skip stage eight')
              // Skip question section if there aren't questions for the current Broker.
              await props.nextStep(4);
            }
            // Because the question section is escaped, the following function (updatePacket) calls the nextStepPatch function simulating the click that would occur in the question section
            console.log('call updatePacket', props.stepTypes[props.formPosition])
            // TODO - Jordan needs to validate the logic of this code
            props.updatePacket(props.stepTypes[props.formPosition]);
          }
        }
        store.commit("firstTime/updateStepFirstTime", {
          step: 'Payment',
          value: false,
        });
      }
      else if (
        props.stepName === "Payment-2" &&
        store.state.payments.payment_type === "factoring"
      ) {
        console.log('second payment screen and user choose factoring')
        let paymentBody = store.state.payments?.payment_body;
        let onboardingPacketId = store.state.onboardingPacket?.id;
        const factorId = store.state.carriers.carrierData?.factor?.id;
        const paymentTermId = store.state.payments?.payment_term_id;

       const isEmpty = (obj) => {
          if (obj === undefined || obj === null || Object.keys(obj).length === 0) {
            return true;
          } else {
            return false;
          }
        }

        if (isEmpty(store.state.payments?.payment_body)){
          const factorBody = {payment_term_id: paymentTermId , factor_id: factorId}
          console.log('Factoring: through Factor.')
          await store.dispatch("factors/assignFactor", {
            paymentBody: factorBody,
            onboardingPacketId,
          });

        }else{
          console.log('Factoring: through PaymentBody.')
          await store.dispatch("factors/assignFactor", {
            paymentBody,
            onboardingPacketId,
          });

        }
        if(!auto_skip_stage_seven) {
          console.log('do not auto skip stage seven')
          await props.nextStep(3);
        } else {
          // We need to request the updated packet again in case company question sections become required
          // via company question section conditions after payment data is submitted
          console.log('requesting updated packet to ensure auto_skip_stage_seven value');
          await store.dispatch("onboardingPacket/getOnboardingPacketById", { "id": store.state.onboardingPacket?.id });
          const recheck_auto_skip_stage_seven = store.state.carriers.carrierData?.auto_skip_stage_seven;
          console.log('recheck_auto_skip_stage_seven', recheck_auto_skip_stage_seven);
          // console.log('updated carrierData');
          // console.dir(store.state.carriers.carrierData);
          if (!recheck_auto_skip_stage_seven) {
            console.log('do NOT auto skip stage seven (after recheck)');
            await props.nextStep(3);
          } else {
            console.log('do auto skip stage seven')
            if (auto_skip_stage_eight) {
              console.log('do auto skip stage eight')
              // Skip question section and integration section if there aren't questions or integrations for the current Broker.
              await props.nextStep(5);
            } else {
              console.log('do NOT auto skip stage eight')
              // Skip question section if there aren't questions for the current Broker.
              await props.nextStep(4);
            }
            // Because the question section is escaped, the following function (updatePacket) calls the nextStepPatch function simulating the click that would occur in the question section
            console.log('call updatePacket', props.stepTypes[props.formPosition])
            // TODO - Jordan needs to validate the logic of this code
            props.updatePacket(props.stepTypes[props.formPosition]);
          }
        }
        store.commit("firstTime/updateStepFirstTime", {
          step: 'Payment',
          value: false,
        });
      } else if (
        props.stepName === "Payment-2" &&
        ((store.state.payments.payment_type === "factoring" &&
          !!props.data?.factor &&
          !store.state.payments.edit.factor) ||
          (store.state.payments.payment_type !== "factoring" &&
            !!props.data?.payment_method &&
            !store.state.payments.edit.payment_method) || store.state.payments.rendered_payment?.[0]?.is_type === 'triumph_pay')
      ) {
        console.log('payment second screen and user did NOT choose factoring')
        console.log('Just for test',store.state.payments?.payment_source ,  store.state.carriers.carrierData?.payment_method?.is_type )
        if(store.state.payments.rendered_payment?.[0]?.is_type === 'triumph_pay' && isPaymentMethodAllowed('triumph_pay')){
          const body = {
                  payment_method_id: store.state.payments.rendered_payment?.[0]?.id,
                  factor_id: null,
            }
            store.dispatch("onboardingPacket/setOnboardingBody", {onboarding_packet: body});
            props.updatePacket(props.stepTypes[props.formPosition]);

        }
        else{
          const paymentMethodId = store.state.payments.activePaymentMethods.find(payment => payment?.is_type === store.state.payments.payment_source)
          if(paymentMethodId){
            const body = {
              payment_method_id: paymentMethodId?.id,
              factor_id: null,
        }
            store.dispatch("onboardingPacket/setOnboardingBody", {onboarding_packet: body});
            props.updatePacket(props.stepTypes[props.formPosition]);

        }
      }

      if(!auto_skip_stage_seven) {
        console.log('do not auto skip stage seven')
        await props.nextStep(3);
      } else {
        // We need to request the updated packet again in case company question sections become required
        // via company question section conditions after payment data is submitted
        console.log('requesting updated packet to ensure auto_skip_stage_seven value');
        await store.dispatch("onboardingPacket/getOnboardingPacketById", { "id": store.state.onboardingPacket?.id });
        const recheck_auto_skip_stage_seven = store.state.carriers.carrierData?.auto_skip_stage_seven;
        console.log('recheck_auto_skip_stage_seven', recheck_auto_skip_stage_seven);
        console.log('updated carrierData');
        console.dir(store.state.carriers.carrierData);
        if (!recheck_auto_skip_stage_seven) {
          console.log('do NOT auto skip stage seven (after recheck)');
          await props.nextStep(3);
        } else {
          console.log('do auto skip stage seven')
          if (auto_skip_stage_eight) {
            console.log('do auto skip stage eight')
            // Skip question section and integration section if there aren't questions or integrations for the current Broker.
            await props.nextStep(5);
          } else {
            console.log('do NOT auto skip stage eight')
            // Skip question section if there aren't questions for the current Broker.
            await props.nextStep(4);
          }
          // Because the question section is escaped, the following function (updatePacket) calls the nextStepPatch function simulating the click that would occur in the question section
          console.log('call updatePacket', props.stepTypes[props.formPosition])
          // TODO - Jordan needs to validate the logic of this code
          props.updatePacket(props.stepTypes[props.formPosition]);
        }
      }
      store.commit("firstTime/updateStepFirstTime", {
        step: 'Payment',
        value: false,
      });
    }
      // else if(props.stepName === "Payment-2" && store.state.payments.rendered_payment?.[0]?.is_type === 'triumph_pay'){
      //   console.log('Comes line 1203')
      //   const body = {
      //             payment_method_id: store.state.payments.rendered_payment?.[0]?.id,
      //             factor_id: null,
      //       }
      //       store.dispatch("onboardingPacket/setOnboardingBody", {onboarding_packet: body});
      //       props.updatePacket(props.stepTypes[props.formPosition]);
      // }
      else if (
        props.stepName === "Payment-3" && store.state.payments.payment_source !== "check"
      ) {
        console.log('entering bank account of check mailing address')
        if (store.state.payments.errors) {
          store.commit("updateField", { name: "errors", value: true });
        } else {
          store.commit("updateField", { name: "errors", value: false });
          const onboardingPacketId = store.state.onboardingPacket?.id;

          const companyType =
            store.state.users.currentUser?.company?.company_type;
          const paymentMethod = store.state.payments?.payment_body;
          if (companyType !== "carrier") {
            paymentMethod.payment_method["onboarding_packet_id"] =
              onboardingPacketId;
          }

          // const verifyIfPaymentMethodExist = store.state.payments.activePaymentMethods
          // const exist = verifyIfPaymentMethodExist.some(payment => payment.is_type === props.data.payment_method.is_type);
          // if(!exist){
          if (props.data?.payment_method || store.state.payments?.verification_id > 0) {
            if(paymentMethod !== undefined){
              paymentMethod.payment_method["verification_id"] =
                store.state.payments.verification_id;
            }
          }

          const paymentResponse = await createPaymentMethod(paymentMethod, onboardingPacketId);
            if(!paymentResponse) return;

            if(!auto_skip_stage_seven) {
              console.log('do not auto skip stage seven')
              await props.nextStep(2);
            } else {
              // We need to request the updated packet again in case company question sections become required
              // via company question section conditions after payment data is submitted
              console.log('requesting updated packet to ensure auto_skip_stage_seven value');
              await store.dispatch("onboardingPacket/getOnboardingPacketById", { "id": store.state.onboardingPacket?.id });
              const recheck_auto_skip_stage_seven = store.state.carriers.carrierData?.auto_skip_stage_seven;
              console.log('recheck_auto_skip_stage_seven', recheck_auto_skip_stage_seven);
              // console.log('updated carrierData');
              // console.dir(store.state.carriers.carrierData);
              if (!recheck_auto_skip_stage_seven) {
                console.log('do NOT auto skip stage seven (after recheck)');
                await props.nextStep(2);
              } else {
                console.log('do auto skip stage seven')
                if (auto_skip_stage_eight) {
                  console.log('do auto skip stage eight')
                  // Skip question section and integration section if there aren't questions or integrations for the current Broker.
                  await props.nextStep(4);
                } else {
                  console.log('do NOT auto skip stage eight')
                  // Skip question section if there aren't questions for the current Broker.
                  await props.nextStep(3);
                }
                // Because the question section is escaped, the following function (updatePacket) calls the nextStepPatch function simulating the click that would occur in the question section
                console.log('call updatePacket', props.stepTypes[props.formPosition])
                // TODO - Jordan needs to validate the logic of this code
                props.updatePacket(props.stepTypes[props.formPosition]);
              }
            }
          store.commit("firstTime/updateStepFirstTime", {
            step: 'Payment',
            value: false,
          });
        }
      } else if (
        props.stepName === "Payment-3" &&
        store.state.payments.payment_source === "check"
      ) {

        const onboardingPacketId = store.state.onboardingPacket?.id;

        const companyType = store.state.users.currentUser?.company?.company_type;
        const paymentMethod = store.state.payments?.payment_body;
        if (companyType !== "carrier") {
          paymentMethod.payment_method["onboarding_packet_id"] =
            onboardingPacketId;
        }
        if (props.data?.payment_method) {
          paymentMethod.payment_method["verification_id"] =
            store.state.payments?.verification_id;
        }

        const paymentResponse = await createPaymentMethod(paymentMethod, onboardingPacketId);
        if(!paymentResponse) return;

        if(!auto_skip_stage_seven) {
            console.log('do not auto skip stage seven')
            await props.nextStep(2);
          } else {
            // We need to request the updated packet again in case company question sections become required
            // via company question section conditions after payment data is submitted
            console.log('requesting updated packet to ensure auto_skip_stage_seven value');
            await store.dispatch("onboardingPacket/getOnboardingPacketById", { "id": store.state.onboardingPacket?.id });
            const recheck_auto_skip_stage_seven = store.state.carriers.carrierData?.auto_skip_stage_seven;
            console.log('recheck_auto_skip_stage_seven', recheck_auto_skip_stage_seven);
            // console.log('updated carrierData');
            // console.dir(store.state.carriers.carrierData);
            if (!recheck_auto_skip_stage_seven) {
              console.log('do NOT auto skip stage seven (after recheck)');
              await props.nextStep(2);
            } else {
              console.log('do auto skip stage seven')
              if (auto_skip_stage_eight) {
                console.log('do auto skip stage eight')
                // Skip question section and integration section if there aren't questions or integrations for the current Broker.
                await props.nextStep(4);
              } else {
                console.log('do NOT auto skip stage eight')
                // Skip question section if there aren't questions for the current Broker.
                await props.nextStep(3);
              }
              // Because the question section is escaped, the following function (updatePacket) calls the nextStepPatch function simulating the click that would occur in the question section
              console.log('call updatePacket', props.stepTypes[props.formPosition])
              // TODO - Jordan needs to validate the logic of this code
              props.updatePacket(props.stepTypes[props.formPosition]);
            }
          }

        // if(!auto_skip_stage_seven) {
        //   await props.nextStep(2);
        // } else {
        //   // Skip question section if there aren't questions for the current Broker.
        //   if (auto_skip_stage_eight) {
        //       await props.nextStep(4);
        //     } else {
        //       await props.nextStep(3);
        //     }

        //   // Because the question section is escaped, the following function (updatePacket) calls the nextStepPatch function simulating the click that would occur in the question section

        //    // TODO - Jordan needs to validate the logic of this code
        //   props.updatePacket(props.stepTypes[props.formPosition]);
        // }
        store.commit("firstTime/updateStepFirstTime", {
          step: 'Payment',
          value: false,
        });
      } else if (props.stepName === "Lanes") {
          console.log('on current stage Lanes')
          props.nextStep();
          store.commit("firstTime/updateStepFirstTime", {
            step: 'Lanes',
            value: false,
          });
      }

      else if(props.stepName === "Insurance-1" && !hasFileAuto.value && hasFileCargo.value){
        console.log('N1')
      props.nextStep(3);
      }

      else if (
        !store.state.firstTime["Insurance"] &&
        props.stepName === "Insurance-1"
        // ||
        // (!store.state.firstTime["Insurance"] &&
        //   props.stepName === "Insurance-3")
      ) {
        console.log('on current stage Insurance-1')
        props.nextStep(2);
      }  else if (
        !store.state.firstTime["Certifications"] &&
        props.stepName === "Certifications-1"
      ) {
        console.log('on current stage Certifications-1')
        props.nextStep(2);
      } else if (e.type === "submit") {
        console.log('props.stepName', props.stepName)
        props.nextStep();
      }
    };*/

    return {
      // companyType,
      // onboardingPacket,
      openErrorModal,
      showErrorModal,
      onCloseModal,
      // onCloseNeedHelpModal,
      showSentModal,
      showNeedHelpModal,
      onCloseSentModal,
      onSuccessError,
      // handleSubmit,
      executeContractAndCompletePacket,
      completePacketAsCarrier,
      finalButtonLoading,
      existSomeTriumphPayMethod,
      isPaymentMethodAllowed,
      isTriumphPayAllowedByPaymentTerms,
      // classifications,
      // identityStepOne,
      // identityStepTwo,
      // operations,
      // lanes,
      // certificationsStepOne,
      // certificationsStepTwo,
      // integrations,
      // contractStepOne,
      // contractStepTwo,
      // paymentStepOne,
      // paymentStepTwo,
      // paymentStepThree,
      // paymentStepFour,
      // insuranceStepOne,
      // insuranceStepTwo,
      // insuranceStepThree,
      // insuranceStepFour,
      // questions,
    };
  },
};
</script>
<style scoped>
@media screen and (max-width: 768px) {
  .box-shadow-top {
    box-shadow: 0px 0px 25px 0px #6f6f6f80;
  }
}

@media screen and (min-width: 768px) {
  .footer-container {
    margin-bottom: 32px !important;
    bottom: 16px !important;
  }
}

.paragraph {
  font-size: 10px;
}

@media screen and (max-width: 680px) {
  .button-container-mobile {
    position: fixed;
    bottom: 0px;
    width: 100%;
  }
  .content-container {
    margin-bottom: 233px;
  }
}
</style>
